import React, { useState, useEffect } from 'react';
import { Storage } from 'aws-amplify';
import './FileViewer.css';

const FileViewer = ({ file, onClose }) => {
  const [fileUrl, setFileUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('iframe'); // iframe, download, preview

  useEffect(() => {
    if (file) {
      loadFile();
    }
  }, [file]);

  // Add keyboard support for closing with Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  const loadFile = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get presigned URL for the file - try all levels starting with public
      let url;
      try {
        // Try public level first (where admin uploads new files)
        url = await Storage.get(file.key, {
          level: 'public',
          expires: 3600, // 1 hour expiry
          download: false
        });
        console.log('File URL generated from public level:', url);
      } catch (publicErr) {
        try {
          // Try protected level (older uploads)
          url = await Storage.get(file.key, {
            level: 'protected',
            expires: 3600, // 1 hour expiry
            download: false
          });
          console.log('File URL generated from protected level:', url);
        } catch (protectedErr) {
          // If protected fails, try private level (oldest uploads)
          url = await Storage.get(file.key, {
            level: 'private',
            expires: 3600, // 1 hour expiry
            download: false
          });
          console.log('File URL generated from private level:', url);
        }
      }

      setFileUrl(url);

      // Determine best view mode based on file type
      const fileType = getFileType(file.name);
      console.log('File type detected:', fileType, 'for file:', file.name);
      console.log('File URL set to:', url);

      if (fileType === 'html') {
        setViewMode('iframe');
      } else if (fileType === 'pdf') {
        setViewMode('iframe');
      } else if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico', 'tiff', 'tif'].includes(fileType)) {
        console.log('Setting view mode to preview for image file');
        setViewMode('preview');
      } else {
        setViewMode('download');
      }
    } catch (err) {
      console.error('Error loading file:', err);
      setError('Failed to load file. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getFileType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    return extension;
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'html': return '🌐';
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'ppt':
      case 'pptx': return '📋';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg': return '🖼️';
      case 'zip':
      case 'rar': return '🗂️';
      default: return '📁';
    }
  };

  const downloadFile = () => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = file.name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const openInNewTab = () => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  };

  if (!file) return null;

  const handleOverlayClick = (e) => {
    // Close if clicking on the overlay (not the container)
    if (e.target.className === 'file-viewer-overlay') {
      onClose();
    }
  };

  return (
    <div className="file-viewer-overlay" onClick={handleOverlayClick}>
      <div className="file-viewer-container">
        <div className="file-viewer-header">
          <div className="file-info">
            <span className="file-icon">{getFileIcon(getFileType(file.name))}</span>
            <h3>{file.name}</h3>
          </div>
          <div className="file-actions">
            <button onClick={openInNewTab} className="action-button">
              Open in New Tab
            </button>
            <button onClick={downloadFile} className="action-button">
              Download
            </button>
            <button onClick={onClose} className="close-button" title="Close">
              ✕ Close
            </button>
          </div>
        </div>

        <div className="file-viewer-content">
          {loading && (
            <div className="loading-container">
              <div className="loader"></div>
              <p>Loading file...</p>
            </div>
          )}

          {error && (
            <div className="error-container">
              <p>❌ {error}</p>
              <button onClick={loadFile}>Try Again</button>
            </div>
          )}

          {!loading && !error && fileUrl && (
            <>
              {viewMode === 'iframe' && (
                <iframe
                  src={fileUrl}
                  title={file.name}
                  className="file-iframe"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                />
              )}

              {viewMode === 'preview' && (
                <div className="image-preview">
                  <img src={fileUrl} alt={file.name} />
                </div>
              )}

              {viewMode === 'download' && (
                <div className="download-prompt">
                  <div className="file-icon-large">
                    {getFileIcon(getFileType(file.name))}
                  </div>
                  <h3>Ready to Download</h3>
                  <p>File: {file.name}</p>
                  <p>Size: {formatFileSize(file.size)}</p>
                  <button onClick={downloadFile} className="download-button">
                    Download File
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {viewMode === 'iframe' && (
          <div className="viewer-footer">
            <p className="viewer-tip">
              💡 For best experience with HTML files, use "Open in New Tab" for full interactivity
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

export default FileViewer;